'use client';

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactElement } from 'react';
import { cn } from '#cn';

/** Radix Dialog's `Root` — the modal state controller. Pair with `DialogContent` (and `DialogTitle`/`DialogDescription`) for the styled panel. */
const Dialog = t0 => {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <DialogPrimitive.Root {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;

// The default centred, transformed panel. A transformed ancestor becomes the
// containing block for any `fixed`/positioned descendant, so consumers that need
// a viewport-anchored child (e.g. a close button at the screen corner) must opt
// out of these classes via `unstyled` and supply their own layout.
const panelClassName = 'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-surface p-6 shadow-2xl focus:outline-none';
type DialogOverlayProps = ComponentProps<typeof DialogPrimitive.Overlay>;
const DialogOverlay = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("fixed inset-0 z-50 bg-black/50", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Overlay className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
type StyledDialogContentProps = {
  /**
   * Drop the centred-panel layout (and the built-in close) so the content can host
   * a bespoke, full-screen focus scope. Defaults to `false`.
   */
  unstyled?: false;
  /**
   * Render a close (X) icon in the top-right corner. Pass `true` so the dialog has
   * a discoverable way to dismiss it, or `false` for dialogs that already expose an
   * explicit dismissal (e.g. a confirmation dialog with a "Cancel" button).
   */
  showClose: boolean;
};
type UnstyledDialogContentProps = {
  unstyled: true;
  /**
   * The built-in close belongs to the centred panel; an unstyled scope composes
   * its own `DialogClose` (e.g. anchored to the viewport corner), so `showClose`
   * is not accepted here.
   */
  showClose?: never;
};
type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  /** Class names merged onto the backdrop overlay, e.g. to override its opacity. */
  overlayClassName?: string;
} & (StyledDialogContentProps | UnstyledDialogContentProps);
const DialogContent = t0 => {
  const $ = _c(23);
  let children;
  let className;
  let overlayClassName;
  let props;
  let showClose;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      showClose,
      unstyled: t1,
      overlayClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = overlayClassName;
    $[4] = props;
    $[5] = showClose;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    overlayClassName = $[3];
    props = $[4];
    showClose = $[5];
    t1 = $[6];
  }
  const unstyled = t1 === undefined ? false : t1;
  let t2;
  if ($[7] !== overlayClassName) {
    t2 = <DialogOverlay className={overlayClassName} />;
    $[7] = overlayClassName;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== className || $[10] !== unstyled) {
    t3 = unstyled ? cn("z-50 focus:outline-none", className) : cn(panelClassName, className);
    $[9] = className;
    $[10] = unstyled;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== showClose || $[13] !== unstyled) {
    t4 = !unstyled && showClose ? <DialogClose aria-label="Close" className="absolute right-4 top-4 grid size-6 place-items-center rounded-full text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-jaaq-black"><X className="size-4 shrink-0" strokeWidth={1.5} /></DialogClose> : null;
    $[12] = showClose;
    $[13] = unstyled;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== children || $[16] !== props || $[17] !== t3 || $[18] !== t4) {
    t5 = <DialogPrimitive.Content className={t3} {...props}>{children}{t4}</DialogPrimitive.Content>;
    $[15] = children;
    $[16] = props;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t2 || $[21] !== t5) {
    t6 = <DialogPortal>{t2}{t5}</DialogPortal>;
    $[20] = t2;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
type DialogTitleProps = ComponentProps<typeof DialogPrimitive.Title>;
const DialogTitle = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("font-black text-fg", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Title className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
type DialogDescriptionProps = ComponentProps<typeof DialogPrimitive.Description>;
const DialogDescription = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-fg-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Description className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogTitle };